import React, { useState, useEffect } from "react";
import {
  Button,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";

const StepDocuments =({ data, onNext, onBack }) => {
  const [files, setFiles] = useState([]);
  const [documentType, setDocumentType] = useState("");

  useEffect(() => {
    if (data.documents) setFiles(data.documents);
    if (data.document_type) setDocumentType(data.document_type);
  }, [data]);

  const handleDrop = (e) => {
    e.preventDefault();
    const newFiles = Array.from(e.dataTransfer.files);
    validateFiles(newFiles);
  };

  const handleSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    validateFiles(newFiles);
  };

  const validateFiles = (fileList) => {
    const validFiles = fileList.filter(
      (file) =>
        (file.type === "application/pdf" || file.type.startsWith("image/")) &&
        file.size <= 5 * 1024 * 1024
    );
    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => setFiles(files.filter((_, i) => i !== index));

  const handleNextStep = () => {
    onNext({ documents: files, document_type: documentType });
  };

  return (
    <div className="flex flex-col items-center space-y-6 text-center">
      <h2 className="text-2xl font-bold text-white neon-text">
        Upload Your Documents
      </h2>
      <p className="text-gray-400">
        Select document type and upload your file
      </p>

      {/* Document Type Dropdown */}
      <FormControl fullWidth className="max-w-md mb-4">
        <InputLabel>Document Type</InputLabel>
        <Select
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          label="Document Type"
          required
        >
          <MenuItem value="passport">Passport</MenuItem>
          <MenuItem value="national_id">National ID</MenuItem>
          <MenuItem value="driver_license">Driver’s License</MenuItem>
          <MenuItem value="utility_bill">Utility Bill</MenuItem>
        </Select>
      </FormControl>

      {/* Drag & Drop Box */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="w-full max-w-md p-10 border-2 border-dashed border-cyan-400 rounded-2xl hover:border-cyan-300 transition-all duration-300 cursor-pointer neon-glow"
      >
        <CloudUploadIcon className="text-cyan-400 text-6xl mb-4 animate-bounce" />
        <p className="text-gray-300">Drop files here or click to upload</p>
        <input
          type="file"
          multiple
          onChange={handleSelect}
          className="hidden"
          id="fileUpload"
          accept="image/*,.pdf"
        />
        <label
          htmlFor="fileUpload"
          className="cursor-pointer text-cyan-400 underline"
        >
          Browse Files
        </label>
      </div>

      {/* File Previews */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        {files.map((file, index) => {
          const isFile = file instanceof File || file instanceof Blob;
          return (
            <div
              key={index}
              className="relative p-3 border border-cyan-400 rounded-xl neon-glow"
            >
              {file.type === "application/pdf" ? (
                <div className="flex flex-col items-center">
                  <PictureAsPdfIcon className="text-red-500 text-4xl" />
                  <p className="text-xs text-gray-300 mt-2 truncate">
                    {file.name}
                  </p>
                </div>
              ) : isFile ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt="preview"
                  className="w-24 h-24 object-cover rounded-lg border border-cyan-300"
                />
              ) : (
                <p className="text-gray-400 text-xs">
                  {file.name || "Invalid file"}
                </p>
              )}

              <Tooltip title="Remove File">
                <DeleteIcon
                  onClick={() => removeFile(index)}
                  className="absolute top-2 right-2 text-red-400 cursor-pointer hover:text-red-600"
                />
              </Tooltip>
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex justify-between w-full max-w-md mt-6">
        <Button variant="outlined" onClick={onBack} className="neon-btn">
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNextStep}
          className="neon-btn"
          disabled={files.length === 0 || !documentType}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default StepDocuments;